import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, ProgressBar, Alert } from 'react-bootstrap';
import { FaCheck, FaChalkboardTeacher, FaUser, FaGraduationCap } from 'react-icons/fa';
import { initializeApp } from 'firebase/app';
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import './insructor.css';

// Firebase configuration
// For security, this should be stored in environment variables
const firebaseConfig = {
    apiKey: "AIzaSyAo8JAiRJUyz6ArJCHOCwEqHQEmfbWgtgA",
    authDomain: "olewin-academy.firebaseapp.com",
    projectId: "olewin-academy",
    storageBucket: "olewin-academy.appspot.com",
    messagingSenderId: "622543323365",
    appId: "1:622543323365:web:9a3e28d5c63850d60e940c",
    measurementId: "G-09WHHRMJRC"
  };
// Initialize Firebase (with a try-catch for error handling)
let app, db;
try {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
} catch (error) {
    console.error("Error initializing Firebase:", error);
}

// Define your component
const InstructorApplicationForm = () => {
  // Define constants and state
  const totalSteps = 4;
  const [currentStep, setCurrentStep] = useState(1);
  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmAccuracy, setConfirmAccuracy] = useState(false);
  
  // Initialize form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    
    highestDegree: '',
    university: '',
    graduationYear: '',
    specialization: '',
    additionalCertifications: '',
    
    yearsOfExperience: '',
    onlineTeachingExperience: false,
    previousInstitutions: '',
    subjectsCapable: [],
    teachingPhilosophy: ''
  });
  
  // Define STEM subjects array
  const stemSubjects = [
    'Mathematics', 'Physics', 'Chemistry', 'Biology', 
    'Life Sciences', 'Engineering', 'English', 'Management',
    'Geology', 'GeoPhysics', 'Bio-Technology',
  ];
  
  // Form input handling
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'subjectsCapable') {
        // Handle multi-select checkboxes
        const updatedSubjects = [...formData.subjectsCapable];
        if (checked) {
          updatedSubjects.push(value);
        } else {
          const index = updatedSubjects.indexOf(value);
          if (index > -1) {
            updatedSubjects.splice(index, 1);
          }
        }
        setFormData({
          ...formData,
          [name]: updatedSubjects
        });
      } else if (name === 'confirmAccuracy') {
        setConfirmAccuracy(checked);
      } else {
        // Handle single checkboxes
        setFormData({
          ...formData,
          [name]: checked
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // Navigation functions
  const nextStep = (e) => {
    e.preventDefault();
    
    const isValid = validateStep(currentStep);
    
    if (isValid) {
      setValidated(false); // Reset validation state for next step
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0); // Scroll to top for next step
    } else {
      setValidated(true);
    }
  };
  
  const prevStep = () => {
    setValidated(false); // Reset validation state when going back
    setCurrentStep(currentStep - 1);
    window.scrollTo(0, 0); // Scroll to top for previous step
  };
  
  // Validate each step specifically
  const validateStep = (step) => {
    switch(step) {
      case 1:
        return formData.firstName && 
               formData.lastName && 
               formData.email && 
               formData.phone &&
               formData.address &&
               formData.city &&
               formData.state &&
               formData.zipCode;
      case 2:
        return formData.highestDegree && 
               formData.university && 
               formData.graduationYear && 
               formData.specialization;
      case 3:
        return formData.yearsOfExperience && 
               formData.subjectsCapable.length > 0 && 
               formData.teachingPhilosophy;
      default:
        return true;
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    
    if (form.checkValidity() === false || !confirmAccuracy) {
      e.stopPropagation();
      setValidated(true);
      if (!confirmAccuracy) {
        setErrorMessage("Please confirm that all information is accurate before submitting.");
        setTimeout(() => setErrorMessage(''), 5000);
      }
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    
    try {
      // Prepare data for Firestore
      const applicationData = {
        personalInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
        },
        educationalBackground: {
          highestDegree: formData.highestDegree,
          university: formData.university,
          graduationYear: formData.graduationYear,
          specialization: formData.specialization,
          additionalCertifications: formData.additionalCertifications,
        },
        teachingExperience: {
          yearsOfExperience: formData.yearsOfExperience,
          onlineTeachingExperience: formData.onlineTeachingExperience,
          previousInstitutions: formData.previousInstitutions,
          subjectsCapable: formData.subjectsCapable,
          teachingPhilosophy: formData.teachingPhilosophy,
        },
        applicationStatus: 'pending',
        submittedAt: serverTimestamp(),
      };
      
      // Add to Firestore collection
      await addDoc(collection(db, "instructorApplications"), applicationData);
      
      setSubmitStatus('success');
    } catch (error) {
      console.error("Error submitting application:", error);
      setSubmitStatus('error');
      setErrorMessage(error.message || 'Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Render form steps
  const renderFormStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4 p-md-5">
              <div className="text-center mb-4">
                <h3>Personal Information</h3>
                <p className="text-muted">Tell us about yourself</p>
              </div>
              
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="firstName">
                    <Form.Label>First Name*</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter your first name"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your first name.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="lastName">
                    <Form.Label>Last Name*</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter your last name"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your last name.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="email">
                    <Form.Label>Email Address*</Form.Label>
                    <Form.Control
                      required
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid email address.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="phone">
                    <Form.Label>Phone Number*</Form.Label>
                    <Form.Control
                      required
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      pattern="^[0-9\-\+\s\(\)]{10,15}$"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid phone number (10-15 digits).
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col xs={12}>
                  <Form.Group controlId="address">
                    <Form.Label>Address*</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      placeholder="Enter your street address"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your address.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="city">
                    <Form.Label>City*</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder="Enter your city"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your city.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group controlId="state">
                    <Form.Label>State*</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      placeholder="State"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your state.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group controlId="zipCode">
                    <Form.Label>Zip Code*</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      placeholder="Zip code"
                      pattern="^\d{5}(-\d{4})?$"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid zip code (XXXXX or XXXXX-XXXX).
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className="bg-white border-0 p-4">
              {errorMessage && (
                <Alert variant="danger" className="mb-3">
                  {errorMessage}
                </Alert>
              )}
              <div className="d-flex justify-content-end">
                <Button 
                  variant="primary" 
                  onClick={nextStep} 
                  className="px-4 py-2"
                >
                  Next: Educational Background
                </Button>
              </div>
            </Card.Footer>
          </Card>
        );
        
      case 2:
        return (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4 p-md-5">
              <div className="text-center mb-4">
                <h3>Educational Background</h3>
                <p className="text-muted">Share your academic qualifications</p>
              </div>
              
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="highestDegree">
                    <Form.Label>Highest Degree Earned*</Form.Label>
                    <Form.Select
                      required
                      name="highestDegree"
                      value={formData.highestDegree}
                      onChange={handleInputChange}
                    >
                      <option value="">Select your highest degree</option>
                      <option value="Bachelor's">Bachelor's Degree</option>
                      <option value="Master's">Master's Degree</option>
                      <option value="PhD">PhD/Doctorate</option>
                      <option value="PostDoc">Post-Doctoral</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select your highest degree.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="university">
                    <Form.Label>University/Institution*</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="university"
                      value={formData.university}
                      onChange={handleInputChange}
                      placeholder="Enter your university or institution"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your university/institution.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="graduationYear">
                    <Form.Label>Graduation Year*</Form.Label>
                    <Form.Control
                      required
                      type="number"
                      name="graduationYear"
                      value={formData.graduationYear}
                      onChange={handleInputChange}
                      placeholder="Enter graduation year"
                      min="1950"
                      max={new Date().getFullYear()}
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid graduation year.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="specialization">
                    <Form.Label>Field of Specialization*</Form.Label>
                    <Form.Control
                      required
                      type="text"
                      name="specialization"
                      value={formData.specialization}
                      onChange={handleInputChange}
                      placeholder="E.g. Organic Chemistry, Computer Science"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your specialization.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col xs={12}>
                  <Form.Group controlId="additionalCertifications">
                    <Form.Label>Additional Certifications</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="additionalCertifications"
                      value={formData.additionalCertifications}
                      onChange={handleInputChange}
                      placeholder="List any additional certifications or qualifications relevant to teaching"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className="bg-white border-0 p-4">
              {errorMessage && (
                <Alert variant="danger" className="mb-3">
                  {errorMessage}
                </Alert>
              )}
              <div className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={prevStep} className="px-4 py-2">
                  Back
                </Button>
                <Button 
                  variant="primary" 
                  onClick={nextStep} 
                  className="px-4 py-2"
                >
                  Next: Teaching Experience
                </Button>
              </div>
            </Card.Footer>
          </Card>
        );
        
      case 3:
        return (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4 p-md-5">
              <div className="text-center mb-4">
                <h3>Teaching Experience</h3>
                <p className="text-muted">Tell us about your teaching background</p>
              </div>
              
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="yearsOfExperience">
                    <Form.Label>Years of Teaching Experience*</Form.Label>
                    <Form.Select
                      required
                      name="yearsOfExperience"
                      value={formData.yearsOfExperience}
                      onChange={handleInputChange}
                    >
                      <option value="">Select your experience</option>
                      <option value="Less than 1 year">Less than 1 year</option>
                      <option value="1-3 years">1-3 years</option>
                      <option value="3-5 years">3-5 years</option>
                      <option value="5-10 years">5-10 years</option>
                      <option value="10+ years">10+ years</option>
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      Please select your years of experience.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group controlId="onlineTeachingExperience">
                    <Form.Label>Online Teaching Experience</Form.Label>
                    <div className="d-flex align-items-center mt-2">
                      <Form.Check
                        type="checkbox"
                        id="onlineTeachingCheck"
                        name="onlineTeachingExperience"
                        checked={formData.onlineTeachingExperience}
                        onChange={handleInputChange}
                        label="I have experience in teaching online"
                      />
                    </div>
                  </Form.Group>
                </Col>
                
                <Col xs={12}>
                  <Form.Group controlId="previousInstitutions">
                    <Form.Label>Previous Teaching Institutions</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="previousInstitutions"
                      value={formData.previousInstitutions}
                      onChange={handleInputChange}
                      placeholder="List schools, colleges, or institutions where you've taught"
                    />
                  </Form.Group>
                </Col>
                
                <Col xs={12}>
                  <Form.Label>Subjects You Can Teach*</Form.Label>
                  <Row className="row-cols-1 row-cols-sm-2 row-cols-md-3 g-2 mb-3">
                    {stemSubjects.map((subject, index) => (
                      <Col key={index}>
                        <Form.Check
                          type="checkbox"
                          id={`subject-${index}`}
                          name="subjectsCapable"
                          value={subject}
                          label={subject}
                          checked={formData.subjectsCapable.includes(subject)}
                          onChange={handleInputChange}
                        />
                      </Col>
                    ))}
                  </Row>
                  {validated && formData.subjectsCapable.length === 0 && (
                    <div className="text-danger small mb-3">
                      Please select at least one subject you can teach.
                    </div>
                  )}
                </Col>
                
                <Col xs={12}>
                  <Form.Group controlId="teachingPhilosophy">
                    <Form.Label>Teaching Philosophy*</Form.Label>
                    <Form.Control
                      required
                      as="textarea"
                      rows={4}
                      name="teachingPhilosophy"
                      value={formData.teachingPhilosophy}
                      onChange={handleInputChange}
                      placeholder="Describe your approach to teaching and educational values"
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide your teaching philosophy.
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
            <Card.Footer className="bg-white border-0 p-4">
              {errorMessage && (
                <Alert variant="danger" className="mb-3">
                  {errorMessage}
                </Alert>
              )}
              <div className="d-flex justify-content-between">
                <Button variant="outline-secondary" onClick={prevStep} className="px-4 py-2">
                  Back
                </Button>
                <Button 
                  variant="primary" 
                  onClick={nextStep} 
                  className="px-4 py-2"
                >
                  Next: Review & Submit
                </Button>
              </div>
            </Card.Footer>
          </Card>
        );
        
      case 4:
        return (
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-4 p-md-5">
              <div className="text-center mb-4">
                <h3>Review & Submit</h3>
                <p className="text-muted">Please review your application before submitting</p>
              </div>
              
              <div className="application-summary">
                <h5 className="border-bottom pb-2 d-flex align-items-center">
                  <FaUser className="me-2 text-primary" /> Personal Information
                </h5>
                <Row className="mb-4 g-3">
                  <Col md={6}>
                    <p className="mb-1 fw-bold">Name</p>
                    <p>{`${formData.firstName} ${formData.lastName}`}</p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1 fw-bold">Contact</p>
                    <p>{formData.email}<br />{formData.phone}</p>
                  </Col>
                  <Col xs={12}>
                    <p className="mb-1 fw-bold">Address</p>
                    <p>{`${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}`}</p>
                  </Col>
                </Row>
                
                <h5 className="border-bottom pb-2 d-flex align-items-center">
                  <FaGraduationCap className="me-2 text-primary" /> Educational Background
                </h5>
                <Row className="mb-4 g-3">
                  <Col md={6}>
                    <p className="mb-1 fw-bold">Degree</p>
                    <p>{`${formData.highestDegree} in ${formData.specialization}`}</p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1 fw-bold">Institution</p>
                    <p>{`${formData.university} (${formData.graduationYear})`}</p>
                  </Col>
                  <Col xs={12}>
                    <p className="mb-1 fw-bold">Additional Certifications</p>
                    <p>{formData.additionalCertifications || 'None specified'}</p>
                  </Col>
                </Row>
                
                <h5 className="border-bottom pb-2 d-flex align-items-center">
                  <FaChalkboardTeacher className="me-2 text-primary" /> Teaching Experience
                </h5>
                <Row className="mb-4 g-3">
                  <Col md={6}>
                    <p className="mb-1 fw-bold">Experience</p>
                    <p>{formData.yearsOfExperience}</p>
                  </Col>
                  <Col md={6}>
                    <p className="mb-1 fw-bold">Online Teaching</p>
                    <p>{formData.onlineTeachingExperience ? 'Yes' : 'No'}</p>
                  </Col>
                  <Col xs={12}>
                    <p className="mb-1 fw-bold">Previous Institutions</p>
                    <p>{formData.previousInstitutions || 'None specified'}</p>
                  </Col>
                  <Col xs={12}>
                    <p className="mb-1 fw-bold">Subjects</p>
                    <p>{formData.subjectsCapable.join(', ')}</p>
                  </Col>
                  <Col xs={12}>
                    <p className="mb-1 fw-bold">Teaching Philosophy</p>
                    <p>{formData.teachingPhilosophy}</p>
                  </Col>
                </Row>
                
                <div className="certification-agreement mb-4">
                  <Form.Check
                    type="checkbox"
                    id="confirmAccuracy"
                    name="confirmAccuracy"
                    checked={confirmAccuracy}
                    onChange={handleInputChange}
                    label="I confirm that all information provided is accurate and complete."
                    className="mb-3"
                    required
                    isInvalid={validated && !confirmAccuracy}
                    feedback="You must confirm the accuracy of your application."
                    feedbackType="invalid"
                  />
                </div>
              </div>
            </Card.Body>
            <Card.Footer className="bg-white border-0 p-4">
              {errorMessage && (
                <Alert variant="danger" className="mb-3">
                  {errorMessage}
                </Alert>
              )}
              
              {submitStatus === 'success' && (
                <Alert variant="success" className="mb-3">
                  <h5><FaCheck className="me-2" /> Application Submitted Successfully!</h5>
                  <p className="mb-0">
                    Thank you for applying to be an instructor. We have received your application and will 
                    review it shortly. You will receive a confirmation email with further instructions.
                  </p>
                </Alert>
              )}
              
              {submitStatus === 'error' && (
                <Alert variant="danger" className="mb-3">
                  <h5>Submission Error</h5>
                  <p className="mb-0">
                    There was a problem submitting your application. Please try again or contact support 
                    if the problem persists.
                  </p>
                </Alert>
              )}
              
              <div className="d-flex justify-content-between">
                <Button 
                  variant="outline-secondary" 
                  onClick={prevStep} 
                  className="px-4 py-2"
                  disabled={isSubmitting}
                >
                  Back
                </Button>
                <Button 
                  variant="success" 
                  type="submit" 
                  onClick={handleSubmit}
                  className="px-4 py-2"
                  disabled={isSubmitting || submitStatus === 'success'}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              </div>
            </Card.Footer>
          </Card>
        );
        
      default:
        return null;
    }
  };
  
  // Main component render
  return (
    <Container className="py-5 my-5">
      <Row className="justify-content-center">
        <Col lg={10} xl={9}>
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold">Instructor Application</h2>
            <p className="lead">Join our teaching team and share your expertise with students worldwide</p>
          </div>
          
          <div className="application-progress mb-4">
            <ProgressBar 
              now={(currentStep / totalSteps) * 100} 
              variant="primary" 
              className="mb-3" 
              style={{ height: '8px' }}
            />
            <div className="d-flex justify-content-between">
              <div className={`step-indicator ${currentStep >= 1 ? 'active' : ''}`}>
                <span className="step-number">1</span>
                <span className="step-text d-none d-sm-inline">Personal Info</span>
              </div>
              <div className={`step-indicator ${currentStep >= 2 ? 'active' : ''}`}>
                <span className="step-number">2</span>
                <span className="step-text d-none d-sm-inline">Education</span>
              </div>
              <div className={`step-indicator ${currentStep >= 3 ? 'active' : ''}`}>
                <span className="step-number">3</span>
                <span className="step-text d-none d-sm-inline">Experience</span>
              </div>
              <div className={`step-indicator ${currentStep >= 4 ? 'active' : ''}`}>
                <span className="step-number">4</span>
                <span className="step-text d-none d-sm-inline">Review</span>
              </div>
            </div>
          </div>
          
          <Form noValidate validated={validated}>
            {renderFormStep()}
          </Form>
          
          <div className="text-center mt-4 text-muted small">
            <p>
              Need help? Contact us at <a href="mailto:olewinacademy@gmail.com">olewinacademy@gmail.com</a>
            </p>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default InstructorApplicationForm;